import { AdmissionTimedOut } from "../api/companySession";
import type { RefusalScreen } from "../components/AdmissionRefusal";
import { getEmbedSessionJwt } from "../config/axios";
import { getPlatform } from "../platform/platformRef";
import { sessionIdentityFromJwt } from "../platform/tenantIdentity";
import { AnalyticsEvents, sourceAnalyticsEvent } from "../util/analytics";
import type { AnalyticsEventProperties } from "../util/eventRegistry";
import { refusalInfo } from "../utils/admissionRefusal";
import { tenantRoute } from "../utils/tenantRoute";

/** What one admission notice or refusal screen on a project URL tab showed, and why. */
export interface AdmissionNotice {
    kind: "first_load" | "renewal";
    /** The failed admission's HTTP status; null for a network failure or the client's own check. */
    status: number | null;
    /** A server refusal code or the client's own reason, never free text. */
    code: string | null;
    /** Whether the tab still held a project credential a request could carry. */
    tokenValid: boolean;
    refusal: RefusalScreen | null;
}

const CODE = /^[a-z][a-z0-9_]{0,63}$/;

/** The server's code on a failed admission, known to the client or not; null when it has none.
 *  An attempt that ran out of time names that, though no server answered. */
export function admissionFailureCode(failure: unknown): string | null {
    if (failure instanceof AdmissionTimedOut) return "admission_timed_out";
    const known = refusalInfo(failure).code;
    if (known) return known;
    const detail = (failure as { response?: { data?: { detail?: unknown } } } | null)?.response
        ?.data?.detail;
    const code = typeof detail === "string" ? detail : (detail as { code?: unknown } | null)?.code;
    return typeof code === "string" && CODE.test(code) ? code : null;
}

function cookieSessionOrganization(): string | null {
    try {
        return getPlatform().auth.stytchClient.organization.getSync()?.organization_slug ?? null;
    } catch {
        return null;
    }
}

/** Sent on the browser's own sign-in and stamped with its organization (`sourceAnalyticsEvent`). */
export async function sendAdmissionNotice(notice: AdmissionNotice): Promise<void> {
    const sourceJwt = await getEmbedSessionJwt(true);
    const orgSlug =
        sessionIdentityFromJwt(sourceJwt)?.organizationSlug ?? cookieSessionOrganization();
    const properties: AnalyticsEventProperties<typeof AnalyticsEvents.ADMISSION_NOTICE> & {
        path: string;
    } = {
        kind: notice.kind,
        status: notice.status,
        code: notice.code,
        token_valid: notice.tokenValid,
        refusal: notice.refusal,
        // Route-relative: the company and project in the URL stay out of the event.
        path: tenantRoute().pathname,
    };
    await sourceAnalyticsEvent(AnalyticsEvents.ADMISSION_NOTICE, orgSlug, properties);
}

const reportedKey = (path: string) => `pp.reportedAdmissionNotice:${path}`;

/** The answer a notice reports. The fresh Document a refusal reloads into gets it again, with
 *  another `kind` and screen ("no access" where the closed one said "lost access"). */
function answerOf(notice: AdmissionNotice): string {
    return JSON.stringify([notice.status, notice.code]);
}

function sessionStore(): Storage | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

/**
 * One `admission_notice` per notice or refusal screen shown, not per retry: the screen on
 * display is remembered, and the retries behind it send nothing until admission succeeds or a
 * different screen replaces it. Across a refusal's reload, the closed Document leaves the answer
 * it reported for the fresh one, whose first notice is skipped only when it reports that same
 * answer. Kept apart from the reload-loop guard (`reloadForRefusal`), which may stop a reload
 * whatever was reported.
 */
export class AdmissionNoticeReporter {
    private shown: string | null = null;
    /** The answer the Document before this one reported as it reloaded into this one; read and
     *  cleared once, at this Document's first notice or admission. */
    private carried: string | null | undefined;

    constructor(
        private readonly send: (notice: AdmissionNotice) => Promise<void> = sendAdmissionNotice,
    ) {}

    show(screen: string, notice: AdmissionNotice): void {
        if (screen === this.shown) return;
        this.shown = screen;
        if (notice.kind === "first_load" && this.takeCarried() === answerOf(notice)) return;
        // Measurement only: a lost event never changes what the tab does.
        void this.send(notice).catch(() => undefined);
    }

    /** `notice`, just reported, closes this Document, which reloads into a fresh one. */
    reportedBeforeReload(notice: AdmissionNotice): void {
        sessionStore()?.setItem(reportedKey(window.location.pathname), answerOf(notice));
    }

    admitted(): void {
        this.shown = null;
        this.takeCarried();
    }

    private takeCarried(): string | null {
        if (this.carried === undefined) {
            const store = sessionStore();
            const key = reportedKey(window.location.pathname);
            this.carried = store?.getItem(key) ?? null;
            store?.removeItem(key);
        } else this.carried = null;
        return this.carried;
    }
}
